import React, { useEffect, useMemo, useState } from 'react';

import { FileImage } from '@/common/constants';
import ScanPreviewContainer from '@/components/elements/DropScan/ScanPreview/ScanPreviewContainer';
import useFilesInDocumentStore, { clearFilesInDocumentStore } from '@/components/elements/DropScan/useFilesInDocument.store';
import { TransactionDocumentsPreviewModal } from '@/modules/employee/TransactionDetails/components/TransactionDocumentsPreview';
import { getFileNameFromPath, getFileType, safeDecodeURIComponent } from '@/utils/modifiers';

interface Props {
  paymentDocumentPath: string;
  modalTitle?: string;
  isNumberOfImageHidden?: boolean;
  isDocPreviewModalVisible?: boolean;
  setIsDocPreviewModalVisible?: (visible: boolean) => void;
}

const PreviewPaymentDocument: React.FC<Props> = ({ 
  paymentDocumentPath, 
  modalTitle, 
  isNumberOfImageHidden,
  isDocPreviewModalVisible: externalIsDocPreviewModalVisible,
  setIsDocPreviewModalVisible: externalSetIsDocPreviewModalVisible
}) => {
  const { handleSetValues, handleReset, values } = useFilesInDocumentStore(paymentDocumentPath);
  const [activeFile, setActiveFile] = useState(0);
  const [internalIsDocPreviewModalVisible, setInternalIsDocPreviewModalVisible] = useState(false);
  const [allFiles, setAllFiles] = useState<FileImage[]>([]);

  const isDocPreviewModalVisible = externalIsDocPreviewModalVisible ?? internalIsDocPreviewModalVisible;
  const setIsDocPreviewModalVisible = externalSetIsDocPreviewModalVisible ?? setInternalIsDocPreviewModalVisible;

  const transactionFiles = useMemo(() => [
    {
      id: paymentDocumentPath,
      path: paymentDocumentPath,
      url: paymentDocumentPath,
      type: getFileType(paymentDocumentPath),
      preview: '',
      name: '',
      temporalId: '',
    }
  ] as FileImage[], [paymentDocumentPath]);

  const currentFileName = useMemo(() => getFileNameFromPath(allFiles?.[activeFile]?.name || allFiles?.[activeFile]?.path), [activeFile, allFiles]);
  const decodedFileName = useMemo(() => safeDecodeURIComponent(currentFileName || ''), [currentFileName]);

  useEffect(() => {
    if (values.files.length && !allFiles.length) {
      setAllFiles(values.files);
    }
  }, [values.files, allFiles]);

  useEffect(() => {
    if (transactionFiles?.length && !values.files.length) {
      handleSetValues({ files: transactionFiles });
    }
  }, [transactionFiles, values.files.length, handleSetValues]);

  useEffect(() => {
    return () => {
      handleReset();
      clearFilesInDocumentStore(paymentDocumentPath);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <ScanPreviewContainer
        storeKey={paymentDocumentPath}
        defaultFiles={allFiles}
        activeFile={activeFile}
        onSetActivePage={setActiveFile}
        onSetPreviewMode={() => setIsDocPreviewModalVisible(true)}
        isRemovingDisabled
        disableUpload
        isSmallViewVersion
        isFlex
        isNumberOfImageHidden={isNumberOfImageHidden}
      />

      <TransactionDocumentsPreviewModal
        visible={isDocPreviewModalVisible}
        onSetVisible={setIsDocPreviewModalVisible}
        modalTitle={modalTitle || decodedFileName}
      >
        <ScanPreviewContainer
          storeKey={paymentDocumentPath}
          defaultFiles={allFiles}
          activeFile={activeFile}
          isShowTooltip={false}
          onSetActivePage={setActiveFile}
          isActiveFileShown
          disableUpload
          isRemovingDisabled
          onSetFiles={setAllFiles}
        />
      </TransactionDocumentsPreviewModal>
    </>
  );
};

export default PreviewPaymentDocument;
